import { activePanelAtom, embeddingsPanelSessionIdAtom } from '@/atoms/panelAtoms';
import {
  sessionPanelInlineEffectiveOpenAtom,
  sessionPanelSheetOpenAtom,
  v2CodesCardOpenRequestAtom,
} from '@/atoms/sessionPageV2Atoms';
import { useSessionWindows } from '@/hooks/query/useQuerySessionWindows';
import { type SessionClinicalEntity } from '@/types';
import { SidePanelState } from '@/types/schema';
import { useArtifactCanvasNavigationOptional } from '@heidi/agents/data';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { useSetAtom } from 'jotai';
import { memo, useCallback } from 'react';
import { CodeItem } from './CodeItem';
import { embeddingsScreenAtom, selectedClinicianCodeAtom } from './EmbeddingsPanel/atoms';
import { EmbeddingsScreen } from './EmbeddingsPanel/types';

interface CodeGroupProps {
  label: string;
  codes: SessionClinicalEntity[];
  tooltipText?: string;
}

const CodeGroup = memo(({ label, codes, tooltipText }: CodeGroupProps) => {
  const sessionId = useCurrentSessionId();
  const setSelectedCode = useSetAtom(selectedClinicianCodeAtom);
  const setEmbeddingsScreen = useSetAtom(embeddingsScreenAtom);
  const setActivePanel = useSetAtom(activePanelAtom);
  const setEmbeddingsPanelSessionId = useSetAtom(embeddingsPanelSessionIdAtom);
  const setSessionPanelOpen = useSetAtom(sessionPanelInlineEffectiveOpenAtom);
  const setSessionPanelSheetOpen = useSetAtom(sessionPanelSheetOpenAtom);
  const setV2CodesCardOpenRequest = useSetAtom(v2CodesCardOpenRequestAtom);
  const { sessionWindows, mergeSessionWindows } = useSessionWindows({
    sessionId,
  });
  const artifactNav = useArtifactCanvasNavigationOptional();

  const closeSplitScreen = useCallback(() => {
    if (sessionWindows?.length === 2) {
      mergeSessionWindows();
    }
  }, [sessionWindows?.length, mergeSessionWindows]);

  // Opens the codes panel in both the V1 (side-panel) and V2 (right-rail
  // collapsible-card) session layouts. The popover lives in the note editor,
  // which is rendered by both; the unread atoms in whichever layout isn't
  // active are no-ops. On V2 we also force the surrounding session panel
  // open — wide-shell uses `sessionPanelOpenAtom`, compact uses
  // `sessionPanelSheetOpenAtom`. Setting the inactive one is harmless.
  const openEmbeddingsPanel = useCallback(() => {
    if (artifactNav?.enabled) {
      artifactNav.openCodes();
      return;
    }

    setActivePanel(SidePanelState.EMBEDDINGS);
    setEmbeddingsPanelSessionId(sessionId);
    closeSplitScreen();
    setSessionPanelOpen(true);
    setSessionPanelSheetOpen(true);
    setV2CodesCardOpenRequest(true);
  }, [
    artifactNav,
    setActivePanel,
    setEmbeddingsPanelSessionId,
    sessionId,
    closeSplitScreen,
    setSessionPanelOpen,
    setSessionPanelSheetOpen,
    setV2CodesCardOpenRequest,
  ]);

  const handleViewCodeDetail = useCallback(
    (code: SessionClinicalEntity) => {
      openEmbeddingsPanel();
      setSelectedCode(code);
      setEmbeddingsScreen(EmbeddingsScreen.CodeDetail);
    },
    [openEmbeddingsPanel, setSelectedCode, setEmbeddingsScreen]
  );

  const handleOpenEmbeddingsList = useCallback(() => {
    openEmbeddingsPanel();
    setSelectedCode(null);
    setEmbeddingsScreen(EmbeddingsScreen.EmbeddingsList);
  }, [openEmbeddingsPanel, setSelectedCode, setEmbeddingsScreen]);

  if (codes.length === 0) {
    return null;
  }

  return (
    <>
      <TypographyP2Bold>{label}</TypographyP2Bold>
      {codes.map((code) => (
        <CodeItem
          key={code.entity_id}
          code={code}
          tooltipText={tooltipText}
          sessionId={sessionId}
          handleViewCodeDetail={handleViewCodeDetail}
          handleOpenEmbeddingsList={handleOpenEmbeddingsList}
        />
      ))}
    </>
  );
});

CodeGroup.displayName = 'CodeGroup';

export default CodeGroup;
